import { useBehavior, useButton } from '@libs/hooks';
import { TButtonData, TButtonDataMessages, TWidget, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ElementType, MouseEvent } from 'react';
import { IButtonStyle } from '../../interface/interface';
import { TButtonOption } from '../../types/options';
import { capitalText, getInlineStyles } from '../../utils/styles';
import { Visual } from '../Image/Visual';
import styles from './styles/buttonRow.module.css';

type TButtonRowWidget<TD, TO> = {
    isWidget: true;
} & TWidgetProps<TD, TO>;

interface TButtonRowComponent extends IButtonStyle {
    isWidget: false;
    uid?: string;
    alignment?: TButtonOption['alignment'];
    hierarchy?: 'primary' | 'destructive';
    iconHierarchy?: 'emphasised' | 'primary' | 'destructive';
    iconVariant?: 'simple' | 'circle';
    iconName?: string;
    label?: string;
    messages?: TButtonDataMessages;
    handleClick?: React.MouseEventHandler;
}

type TButtonRow<TD, TO> = (TButtonRowWidget<TD, TO> | TButtonRowComponent) & {
    as?: ElementType;
};

export function ButtonRow(props: TButtonRow<TButtonData, TButtonOption>) {
    const { isWidget, as } = props;
    const finalWidget = isWidget
        ? props.widget
        : ({
              uid: props.uid,
              data: {
                  messages: {
                      label: props.label,
                  },
              },
              options: {
                  iconName: props.iconName,
                  iconVariant: props.iconVariant,
                  isDisabled: props.isDisabled,
                  isHide: props.isHide,
                  size: 'small',
                  alignment: props.alignment ?? '',
                  hierarchy: props.hierarchy,
                  margin: {
                      top: props.marginTop,
                      left: props.marginLeft,
                      right: props.marginRight,
                      bottom: props.marginBottom,
                  },
              },
          } as TWidget<TButtonData, TButtonOption>);

    const label = () => finalWidget.data?.messages?.label ?? finalWidget.data?.label;
    const { handleClick } = useButton(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget);

    const iconName = finalWidget.options?.iconName;
    const size = finalWidget.options?.size;
    const hierarchy = finalWidget.options?.hierarchy;
    const margin = finalWidget.options?.margin;
    const ButtonComponent = as ?? 'button';

    const className = clsx(
        styles.buttonRow,
        isDisabled && styles.disabled,
        styles[hierarchy ?? ''],
        styles[`alignment${capitalText(finalWidget.options?.alignment)}`],
        size === 'large' && iconName && styles.sizeLargeWithIcon,
        size === 'large' && !iconName && styles.sizeLarge,
        styles[`color${capitalText(hierarchy === 'destructive' ? 'destructive' : '')}${capitalText(isDisabled ? 'disabled' : '')}`],
    );

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <ButtonComponent
                data-test={isWidget ? props.widget?.uid : props.uid}
                aria-disabled={isDisabled}
                aria-label={label()}
                aria-hidden={isHidden}
                className={className}
                disabled={isDisabled}
                style={getInlineStyles({ margin })}
                onClick={(e: MouseEvent) => {
                    if (isWidget) {
                        handleClick();
                    } else {
                        props?.handleClick?.(e);
                    }
                }}
            >
                {iconName && (
                    <Visual
                        isWidget={false}
                        // isEnabled={!isDisabled}
                        isDisabled={isDisabled}
                        iconName={iconName}
                        rightMargin
                        iconHierarchy={finalWidget.options?.iconHierarchy}
                        iconVariant={finalWidget.options?.iconVariant}
                    />
                )}
                <span>{label()}</span>
            </ButtonComponent>
        </Activity>
    );
}
